import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Quote, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { StarRow, serviceLabel, formatLocation } from "@/components/review-utils";

export default function FeaturedReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    api.get("/reviews/featured", { params: { limit: 4 } })
      .then(({ data }) => { if (mounted) setReviews(data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading || reviews.length === 0) {
    // Don't render the whole section if there's nothing to show
    if (!loading && reviews.length === 0) {
      return (
        <section data-testid="featured-reviews-empty" className="relative py-28 md:py-36 border-t border-white/5">
          <div className="max-w-5xl mx-auto px-6 md:px-10 lg:px-16 text-center">
            <div className="overline mb-4">— Client Stories</div>
            <h2 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Real families. <span className="italic text-[#D4AF37]">Real impact.</span>
            </h2>
            <p className="text-white/65 max-w-xl mx-auto mb-8 leading-relaxed">
              We're gathering stories from the families and agents we serve. Be the first to share yours.
            </p>
            <Link to="/reviews" data-testid="featured-empty-cta" className="btn-secondary">
              Leave a Review <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      );
    }
    return null;
  }

  return (
    <section data-testid="featured-reviews" className="relative py-28 md:py-36 border-t border-white/5">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_rgba(212,175,55,0.05),_transparent_60%)]" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="overline mb-4">— Client Stories</div>
            <h2 className="font-serif text-4xl md:text-6xl text-white leading-tight tracking-tight">
              Real families. <span className="italic text-[#D4AF37]">Real impact.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 flex lg:justify-end">
            <Link to="/reviews" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs uppercase tracking-[0.22em] border-b border-[#D4AF37]/40 pb-1 hover:gap-4 transition-all">
              View All Reviews <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {reviews.slice(0, 4).map((r) => (
            <article key={r.id} data-testid={`featured-review-${r.id}`} className="card-luxury p-8 md:p-10 relative">
              <Quote className="absolute top-6 right-6 w-10 h-10 text-[#D4AF37]/15" strokeWidth={1} />
              <StarRow value={r.rating} className="mb-5" />
              <p className="font-serif text-xl md:text-2xl text-white leading-snug mb-6">"{r.text}"</p>
              <div className="flex items-center justify-between pt-5 border-t border-white/5">
                <div>
                  <div className="text-white font-medium">{r.name}</div>
                  {formatLocation(r) && <div className="text-xs text-white/40 tracking-wide">{formatLocation(r)}</div>}
                </div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#D4AF37]">{serviceLabel(r.service)}</div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
